import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  DEFAULT_HARMONY_MODE,
  HARMONY_MODE_OPTIONS,
  generateHarmonicPalette,
  normalizeHex,
  normalizePaletteColorList,
} from "../lib/harmonicPalette";
import * as api from "../lib/api";
import ColorField from "./ColorField";
import ColorBlockPicker from "./ColorBlockPicker";

function randomPaletteDialsForHarmony(harmonyMode) {
  const pick = (a, b) => a + Math.random() * (b - a);
  return {
    harmonyMode,
    paletteStructure: pick(0.06, 0.78),
    temperature: pick(-0.55, 0.55),
    vibrancy: pick(-0.45, 0.55),
    spread: pick(-0.5, 0.55),
    lightnessBias: pick(-0.4, 0.45),
    chroma: pick(-0.35, 0.45),
    preserveContrast: pick(-0.25, 0.35),
  };
}

function pickHarmonyRowsFromPalettesResponse(data, harmonyMode) {
  if (!data || typeof data !== "object") return null;
  const mode = String(harmonyMode || "");
  const candidates = [
    mode,
    mode.replace(/-/g, "_"),
    mode === "split" ? "split-complementary" : null,
    mode === "split" ? "split_complementary" : null,
    mode === "split" ? "splitComplementary" : null,
  ].filter(Boolean);
  for (const k of candidates) {
    const rows = data[k];
    if (Array.isArray(rows) && rows.length) return rows;
  }
  const modeNorm = mode.toLowerCase().replace(/-/g, "");
  for (const k of Object.keys(data)) {
    if (!Array.isArray(data[k]) || !data[k].length) continue;
    const kn = k.toLowerCase().replace(/-/g, "");
    if (kn === modeNorm) return data[k];
  }
  for (const k of Object.keys(data)) {
    if (Array.isArray(data[k]) && data[k].length) return data[k];
  }
  return null;
}

const PalettesPanel = ({
  palettes,
  selectedPaletteId,
  isLoading,
  canApplyToPattern,
  onSelectPalette,
  onCreatePalette,
  onRenamePalette,
  onDeletePalette,
  onSaveColors,
  onApplyToPattern,
}) => {
  const [newName, setNewName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [draftColors, setDraftColors] = useState([]);
  const [draftPaletteId, setDraftPaletteId] = useState(null);
  const [colorCountText, setColorCountText] = useState("4");
  const [harmonyMode, setHarmonyMode] = useState(DEFAULT_HARMONY_MODE);
  const [baseColorEnabled, setBaseColorEnabled] = useState(false);
  const [baseColor, setBaseColor] = useState("#5b8cff");
  const [palettesProLoading, setPalettesProLoading] = useState(false);
  const [palettesProError, setPalettesProError] = useState(null);

  const selectedPalette = useMemo(
    () =>
      palettes.find((palette) => String(palette.id) === String(selectedPaletteId)) ||
      null,
    [palettes, selectedPaletteId]
  );

  const serverColors = useMemo(
    () => normalizePaletteColorList(selectedPalette?.colors),
    [selectedPalette?.colors]
  );

  useEffect(() => {
    setRenameValue(selectedPalette?.name || "");
  }, [selectedPalette?.id, selectedPalette?.name]);

  useEffect(() => {
    const colors = normalizePaletteColorList(selectedPalette?.colors);
    setDraftColors(colors);
    setColorCountText(String(Math.max(colors.length, 1)));
    setDraftPaletteId(selectedPalette?.id ?? null);
    // Re-seed only when switching palettes. Saving colors reloads the list;
    // resetting on that echo remounts native pickers and closes them.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- palette id only
  }, [selectedPalette?.id]);

  const workshopColors =
    draftPaletteId === selectedPalette?.id && draftColors.length > 0
      ? draftColors
      : serverColors;

  const basePreviewHex = useMemo(() => {
    const hex = normalizeHex(baseColor) || String(baseColor || "").trim().toLowerCase();
    return (hex || "#5b8cff").toUpperCase();
  }, [baseColor]);

  const rollsForPalette = useCallback(
    () => ({
      ...randomPaletteDialsForHarmony(harmonyMode),
      baseColor:
        baseColorEnabled && normalizeHex(baseColor)
          ? normalizeHex(baseColor)
          : null,
    }),
    [harmonyMode, baseColorEnabled, baseColor]
  );

  const persistColors = (colors) => {
    if (!selectedPalette) return;
    const next = normalizePaletteColorList(colors);
    setDraftColors(next);
    setDraftPaletteId(selectedPalette.id);
    onSaveColors(selectedPalette.id, next);
  };

  const handleCreate = (event) => {
    event.preventDefault();
    const trimmed = newName.trim();
    if (!trimmed) return;
    const colors = generateHarmonicPalette(4, rollsForPalette());
    onCreatePalette(trimmed, colors);
    setNewName("");
    setIsCreating(false);
  };

  const submitRename = (event) => {
    event.preventDefault();
    const trimmed = renameValue.trim();
    if (!trimmed || !selectedPalette) return;
    if (trimmed === selectedPalette.name) return;
    onRenamePalette(selectedPalette.id, trimmed);
  };

  const handleRollColors = () => {
    const parsed = parseInt(colorCountText, 10);
    const count = Math.max(1, Math.min(26, Number.isFinite(parsed) ? parsed : 4));
    setColorCountText(String(count));
    persistColors(generateHarmonicPalette(count, rollsForPalette()));
  };

  const handlePalettesProBaseColor = async () => {
    setPalettesProError(null);
    setPalettesProLoading(true);
    try {
      const seed = normalizeHex(baseColor) || String(baseColor || "").trim();
      const colorForApi = seed || "#5b8cff";
      const schemes = [
        harmonyMode,
        "analogous",
        "complementary",
        "triadic",
        "split-complementary",
        "monochromatic",
      ]
        .filter((value, index, list) => list.indexOf(value) === index)
        .join(",");
      const data = await api.fetchPalettesColorHarmony({
        color: colorForApi,
        schemes,
        formats: "hex",
      });
      const rows = pickHarmonyRowsFromPalettesResponse(data, harmonyMode);
      if (!rows?.length) {
        throw new Error("Palettes response had no color rows for this harmony mode");
      }
      const firstHex = rows
        .map((row) => (row && typeof row === "object" ? row.hex : null))
        .find(Boolean);
      if (!firstHex) {
        throw new Error("Palettes rows did not include hex values");
      }
      setBaseColorEnabled(true);
      setBaseColor(normalizeHex(firstHex) || firstHex);
    } catch (err) {
      setPalettesProError(err.message || String(err));
    } finally {
      setPalettesProLoading(false);
    }
  };

  if (!selectedPalette) {
    return (
      <div className="panel__content">
        <div className="collections-toolbar">
          <p className="collections-toolbar__count">
            {palettes.length} {palettes.length === 1 ? "palette" : "palettes"}
          </p>
          <button
            type="button"
            className="button button--primary"
            onClick={() => setIsCreating(true)}
          >
            Create New Palette
          </button>
        </div>

        {isCreating && (
          <form className="collections-create" onSubmit={handleCreate}>
            <label htmlFor="new-palette-name">Palette name</label>
            <div className="collections-create__row">
              <input
                id="new-palette-name"
                type="text"
                className="number-input collections-create__input"
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                placeholder="e.g. desert sunset"
                autoFocus
              />
              <button
                type="submit"
                className="button"
                disabled={isLoading || !newName.trim()}
              >
                Create
              </button>
              <button
                type="button"
                className="button"
                onClick={() => {
                  setIsCreating(false);
                  setNewName("");
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {palettes.length === 0 ? (
          <p className="collections-empty">No palettes yet.</p>
        ) : (
          <ul className="collection-cards">
            {palettes.map((palette) => {
              const colors = normalizePaletteColorList(palette.colors);
              const count = colors.length;
              return (
                <li key={palette.id}>
                  <button
                    type="button"
                    className="collection-card"
                    onClick={() => onSelectPalette(String(palette.id))}
                  >
                    <span className="collection-card__name">{palette.name}</span>
                    <span className="collection-card__count">
                      {count} {count === 1 ? "color" : "colors"}
                    </span>
                    <span className="palette-card__swatches" aria-hidden="true">
                      {colors.slice(0, 8).map((color) => (
                        <span
                          key={color}
                          className="palette-card__swatch"
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className="panel__content">
      <div className="collections-detail__top">
        <button
          type="button"
          className="button button--compact"
          onClick={() => onSelectPalette("")}
        >
          ← All palettes
        </button>
      </div>

      <form className="collections-rename" onSubmit={submitRename}>
        <label htmlFor="rename-palette-name">Palette name</label>
        <div className="collections-create__row">
          <input
            id="rename-palette-name"
            type="text"
            className="number-input collections-create__input"
            value={renameValue}
            onChange={(event) => setRenameValue(event.target.value)}
          />
          <button
            type="submit"
            className="button"
            disabled={
              isLoading ||
              !renameValue.trim() ||
              renameValue.trim() === selectedPalette.name
            }
          >
            Save name
          </button>
          <button
            type="button"
            className="button"
            onClick={() => onDeletePalette(selectedPalette.id, selectedPalette.name)}
          >
            Delete palette
          </button>
        </div>
      </form>

      <section className="pg-section">
        <h3 className="pg-section__title">Colors</h3>
        <ul className="palette-workshop__colors">
          {workshopColors.map((color, index) => (
            <li key={index} className="palette-workshop__row">
              <ColorField
                color={color}
                colorLabel={`Palette color ${index + 1}`}
                hexLabel={`Palette color ${index + 1} hex`}
                onCommit={(nextHex) => {
                  const next = [...workshopColors];
                  next[index] = nextHex;
                  persistColors(next);
                }}
              />
              <button
                type="button"
                className="button button--compact"
                onClick={() =>
                  persistColors(workshopColors.filter((_, i) => i !== index))
                }
                disabled={workshopColors.length <= 1}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
        <div className="pg-section__actions">
          <button
            type="button"
            className="button"
            onClick={() => persistColors([...workshopColors, "#0066ff"])}
          >
            Add Color
          </button>
          <button
            type="button"
            className="button button--primary"
            onClick={() => onApplyToPattern(workshopColors)}
            disabled={!canApplyToPattern || workshopColors.length === 0}
          >
            Apply to pattern
          </button>
        </div>
      </section>

      <div className="recolor-panel pattern-generator-recolor">
        <div className="recolor-panel__top">
          <div>
            <p className="recolor-panel__eyebrow">Generate colors</p>
          </div>
          <div className="recolor-panel__actions">
            <button
              type="button"
              className="button button--primary button--compact recolor-panel__cta"
              onClick={handleRollColors}
              title="Roll a new set of colors using the harmony and base settings below."
            >
              Roll colors
            </button>
          </div>
        </div>

        <div className="recolor-field">
          <label className="recolor-field__label" htmlFor="palette-color-count">
            Number of colors
          </label>
          <input
            id="palette-color-count"
            type="text"
            inputMode="numeric"
            className="color-text-input"
            value={colorCountText}
            onChange={(event) => {
              const next = event.target.value;
              if (next === "" || /^[0-9]+$/.test(next)) {
                setColorCountText(next);
              }
            }}
            onBlur={() => {
              const parsed = parseInt(colorCountText, 10);
              setColorCountText(
                String(Math.max(1, Math.min(26, Number.isFinite(parsed) ? parsed : 4)))
              );
            }}
          />
        </div>

        <div className="recolor-field">
          <label className="recolor-field__label" htmlFor="palette-harmony">
            Color harmony
          </label>
          <select
            id="palette-harmony"
            className="select-input recolor-panel__select"
            value={harmonyMode}
            onChange={(event) => setHarmonyMode(event.target.value)}
            aria-label="Color harmony starting point"
            title={
              HARMONY_MODE_OPTIONS.find((option) => option.id === harmonyMode)
                ?.hint || ""
            }
          >
            {HARMONY_MODE_OPTIONS.map((option) => (
              <option key={option.id} value={option.id} title={option.hint}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="recolor-base">
          <label className="recolor-base__toggle">
            <input
              type="checkbox"
              checked={baseColorEnabled}
              onChange={(event) => {
                const enabled = event.target.checked;
                setBaseColorEnabled(enabled);
                if (enabled && workshopColors[0]) {
                  setBaseColor(workshopColors[0]);
                }
              }}
            />
            <span>
              Base harmony on a chosen color (other palette colors follow this
              hue and saturation)
            </span>
          </label>
          <div className="recolor-base__palettes-pro" style={{ marginTop: 8 }}>
            <button
              type="button"
              className="button button--compact"
              onClick={handlePalettesProBaseColor}
              disabled={palettesProLoading}
              title="Uses the current base color as the seed; sets the picker to the first swatch Palettes Pro returns for the harmony mode above."
            >
              {palettesProLoading ? "Palettes…" : "Refine base (Palettes Pro)"}
            </button>
            {palettesProError && (
              <p className="modal-error" role="alert" style={{ marginTop: 8 }}>
                {palettesProError}
              </p>
            )}
          </div>
          {baseColorEnabled && (
            <div className="recolor-base__pickers">
              <div className="recolor-base__preview" aria-live="polite">
                <div
                  className="recolor-base__preview-swatch"
                  style={{ backgroundColor: basePreviewHex }}
                  title={basePreviewHex}
                  role="img"
                  aria-label={`Selected base color ${basePreviewHex}`}
                />
                <div className="recolor-base__preview-meta">
                  <span className="recolor-base__preview-label">Selected</span>
                  <ColorField
                    color={normalizeHex(baseColor) || "#5b8cff"}
                    colorLabel="Base harmony color"
                    hexLabel="Base color hex"
                    showColorInput={false}
                    onCommit={setBaseColor}
                  />
                </div>
              </div>
              <ColorBlockPicker
                value={normalizeHex(baseColor) || "#5b8cff"}
                extraColors={[baseColor]}
                onSelect={setBaseColor}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PalettesPanel;
